import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiCopy, FiCheck, FiCode, FiExternalLink } = FiIcons;

const EmbedModal = ({ isOpen, onClose, embedPath, width = "100%", height = "450px" }) => {
  const [copied, setCopied] = useState(false);

  // FIXED: Reliable URL generation for HashRouter (avoids double slashes)
  const getFullUrl = () => {
    const origin = window.location.origin;
    // Get the base path (e.g., / or /project-name/)
    const base = window.location.pathname.replace(/\/$/, '');
    // Ensure the hash path is correctly formatted
    const hash = embedPath.startsWith('/') ? embedPath : `/${embedPath}`;
    
    return `${origin}${base}/#${hash}`;
  };

  const fullEmbedUrl = getFullUrl();
  const embedCode = `<iframe src="${fullEmbedUrl}" width="${width}" height="${height}" frameborder="0" allow="autoplay" scrolling="no" style="border:none; overflow:hidden; background:transparent;"></iframe>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden"
          >
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00AEEF] text-white flex items-center justify-center">
                  <SafeIcon icon={FiCode} className="text-xl" />
                </div>
                <div>
                  <h3 className="font-black text-gray-900 uppercase tracking-tight">Embed Options</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Copy code below</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                <SafeIcon icon={FiX} className="text-gray-500" />
              </button>
            </div>

            <div className="p-8">
              <div className="mb-6">
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-3">Iframe Code</p>
                <div className="relative group">
                  <pre className="bg-gray-900 text-cyan-400 p-6 rounded-2xl text-[11px] overflow-x-auto font-mono leading-relaxed border border-gray-800 whitespace-pre-wrap h-32 select-all">
                    {embedCode}
                  </pre>
                  <button 
                    onClick={handleCopy}
                    className="absolute top-4 right-4 flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-[10px] font-bold uppercase tracking-widest backdrop-blur-md transition-all"
                  >
                    <SafeIcon icon={copied ? FiCheck : FiCopy} />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                  <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Dimensions</p>
                  <p className="text-xs font-bold text-gray-700">{width} × {height}</p>
                </div>
                <button 
                  onClick={() => window.open(fullEmbedUrl, '_blank')}
                  className="flex items-center justify-center gap-2 bg-[#F0F9FF] hover:bg-[#E0F2FE] text-[#00AEEF] rounded-2xl p-4 font-bold text-xs transition-colors border border-[#BAE6FD]"
                >
                  <SafeIcon icon={FiExternalLink} /> Preview
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EmbedModal;